import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Form, Modal, notification, Typography } from 'antd';

import {
  CompanyPartStockPricesRequest,
  PartStockPricesResult,
} from '@/core/domain/stock/StockItems/StockItems.types';

import CompanyPartStockPricesForm, {
  CompanyPartStockPricesFormData,
} from '../CompanyPartStockPricesForm';
import { useDefaultFormErrorNotification, useRouteMatchParam } from '@/hooks';
import { parseCurrency } from '@/utils/number';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { CostItemsFieldsOptions } from '@/core/domain/stock/Configuration/Configuration.types';
import { useCompanyPartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useCompanyStockConfiguration } from '@/core/domain/stock/Configuration/Configuration.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

const { Paragraph } = Typography;

interface CompanyPartStockPricesModalProps {
  visible: boolean;
  handleVisible: (prices?: PartStockPricesResult) => void;
  partStockPrices?: PartStockPricesResult;
}

function CompanyPartStockPricesModal({
  visible,
  handleVisible,
  partStockPrices,
}: CompanyPartStockPricesModalProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<CompanyPartStockPricesFormData>();

  const partId = +useRouteMatchParam('partId');

  const { data, dataUpdatedAt, isLoading } = useCompanyStockConfiguration(
    visible ? partStockPrices?.company.id : undefined,
  );

  const mutation = useCompanyPartStockPrices();

  useEffect(() => {
    if (partStockPrices && data?.result) {
      form.setFieldsValue({
        ...partStockPrices,
        ipi: partStockPrices.ipi * 100,
        fixedCost: (data.result.fixedCost || 0) * 100,
        profitMargin: partStockPrices.profitMargin * 100,
        maxDiscount: partStockPrices.maxDiscount * 100,
        sellPrice: partStockPrices.sellPrice,
        commissionPercentage: partStockPrices.commissionPercentage * 100,
      });
    }
  }, [partStockPrices, data]);

  const handleOk = async () => {
    try {
      if (!partStockPrices) return;

      const values = await form.validateFields();

      const req: CompanyPartStockPricesRequest = {
        ...values,
        partId,
        companyId: partStockPrices?.company.id,
        minStock: values.minStock,
        maxStock: values.maxStock,
        buyPrice: parseCurrency(values.buyPrice),
        ipi: values.ipi ? values.ipi / 100 : 0,
        taxReplacement: parseCurrency(values.taxReplacement),
        freightCost: parseCurrency(values.freightCost),
        otherExpenses: parseCurrency(values.otherExpenses),
        location: values.location,
        icmsExemption: parseCurrency(values.icmsExemption),
        sellPrice: parseCurrency(values.sellPrice),
        maxDiscount: values.maxDiscount ? values.maxDiscount / 100 : 0,
        commissionPercentage: (values.commissionPercentage || 0) / 100,
      };

      await mutation.mutateAsync(req);

      notification.success({
        message: intl.formatMessage({
          id: 'parts.stock.update.notifications.success.title',
        }),
        description: intl.formatMessage({
          id: 'parts.stock.update.notifications.success.description',
        }),
      });

      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    handleVisible();
  };

  return (
    <Modal
      title={`${intl.formatMessage({ id: 'parts.stock' })}: ${
        partStockPrices?.company.name
      }`}
      open={visible}
      onOk={handleOk}
      okText="Salvar"
      okButtonProps={{
        disabled: !hasPermissions(
          [
            Authorities.REGISTER_PART_EDIT_PRODUCT_STOCK,
            Authorities.REGISTER_PART_EDIT_PRODUCT_PRICES,
          ],
          true,
        ),
      }}
      confirmLoading={mutation.isLoading}
      onCancel={handleCancel}
      width={765}
      destroyOnClose
      style={{ position: 'relative' }}
    >
      {isLoading && (
        <ModalLoadingOverlay
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: 8,
          }}
        />
      )}
      <CompanyPartStockPricesForm
        form={form}
        costFields={partStockPrices?.costFields}
        key={dataUpdatedAt}
      />

      <Divider />

      <Paragraph>
        <FormattedMessage
          id="parts.stock.enabledCostFields"
          values={{ company: partStockPrices?.company.name }}
        />
      </Paragraph>

      <Paragraph>
        <ul>
          {partStockPrices?.costFields
            ?.map((a) =>
              a.active ? (
                <li key={a.field}>
                  <FormattedMessage
                    id={`companies.settings.priceComposition.domain.enums.item_field_calculate.${
                      CostItemsFieldsOptions[a.field]
                    }`}
                  />
                </li>
              ) : null,
            )
            .filter((a) => a)}
        </ul>
      </Paragraph>
    </Modal>
  );
}

export default CompanyPartStockPricesModal;
